import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../api/client';
import { CURRENCY } from '../config/constants';
import { Loader, X, Trash2 } from 'lucide-react';
import { istToday, formatIstDate } from '../utils/formatIst';

const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
];

const money = (v) => `${CURRENCY.SYMBOL}${parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  paid: 'bg-green-100 text-green-700',
  partial: 'bg-amber-100 text-amber-700',
  unpaid: 'bg-red-100 text-red-700',
};

const OrderPaymentsModal = ({ order, canDelete, onClose, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({
    amount: '',
    paid_on: istToday(),
    method: 'cash',
    reference_no: '',
    note: '',
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getOrderPayments(order.id);
      setPayments(res.data.payments || []);
      setSummary(res.data.summary || null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [order.id]);

  useEffect(() => { load(); }, [load]);

  const applySummary = (data) => {
    setPayments(data.payments || []);
    setSummary(data.summary || null);
    if (onChanged) onChanged();
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Enter a valid amount greater than zero.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const res = await adminAPI.addOrderPayment(order.id, {
        amount: amt,
        paid_on: form.paid_on,
        method: form.method,
        reference_no: form.reference_no || null,
        note: form.note || null,
      });
      applySummary(res.data);
      setForm({ amount: '', paid_on: istToday(), method: 'cash', reference_no: '', note: '' });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Remove this payment receipt?')) return;
    try {
      setSaving(true);
      setError(null);
      const res = await adminAPI.deleteOrderPayment(paymentId);
      applySummary(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to remove payment');
    } finally {
      setSaving(false);
    }
  };

  const fillBalance = () => {
    if (summary) setForm((f) => ({ ...f, amount: String(summary.balance || 0) }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Payments — Order #{order.id}</h3>
            <p className="text-sm text-gray-600">
              {order.customer_name || 'Customer'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="m-5 mb-0 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="p-10 flex justify-center">
            <Loader className="w-7 h-7 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {summary && (
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <p className="text-xs text-gray-500 uppercase font-semibold">Order total</p>
                  <p className="text-lg font-bold text-gray-900">{money(summary.order_amount)}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50 border border-green-100">
                  <p className="text-xs text-green-600 uppercase font-semibold">Paid</p>
                  <p className="text-lg font-bold text-green-700">{money(summary.amount_paid)}</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                  <p className="text-xs text-amber-600 uppercase font-semibold">Balance</p>
                  <p className="text-lg font-bold text-amber-700">{money(summary.balance)}</p>
                </div>
              </div>
            )}
            {summary && (
              <div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[summary.payment_status] || 'bg-gray-100 text-gray-700'}`}>
                  {summary.payment_status}
                </span>
              </div>
            )}

            <form onSubmit={handleAdd} className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-blue-900">Record a payment</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Amount *</label>
                  <div className="flex gap-2">
                    <input
                      type="number" min="0" step="0.01"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <button type="button" onClick={fillBalance} className="px-3 py-2 text-xs font-semibold text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 whitespace-nowrap">
                      Balance
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Date *</label>
                  <input
                    type="date"
                    value={form.paid_on}
                    onChange={(e) => setForm({ ...form, paid_on: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Method</label>
                  <select
                    value={form.method}
                    onChange={(e) => setForm({ ...form, method: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Reference no.</label>
                  <input
                    type="text"
                    value={form.reference_no}
                    onChange={(e) => setForm({ ...form, reference_no: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Txn / cheque no."
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Note</label>
                <input
                  type="text"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional"
                />
              </div>
              <button type="submit" disabled={saving} className="bg-green-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-60">
                {saving ? 'Saving…' : 'Add payment'}
              </button>
            </form>

            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Receipts ({payments.length})</p>
              {payments.length === 0 ? (
                <p className="text-sm text-gray-500">No payments recorded yet.</p>
              ) : (
                <div className="overflow-x-auto border border-gray-100 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                      <tr>
                        <th className="text-left font-semibold px-3 py-2">Date</th>
                        <th className="text-right font-semibold px-3 py-2">Amount</th>
                        <th className="text-left font-semibold px-3 py-2">Method</th>
                        <th className="text-left font-semibold px-3 py-2">Reference</th>
                        <th className="text-left font-semibold px-3 py-2">By</th>
                        {canDelete && <th className="px-3 py-2" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {payments.map((p) => (
                        <tr key={p.id} className="text-gray-800">
                          <td className="px-3 py-2 whitespace-nowrap">{formatIstDate(p.paid_on)}</td>
                          <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{money(p.amount)}</td>
                          <td className="px-3 py-2 capitalize whitespace-nowrap">{String(p.method || '').replace('_', ' ')}</td>
                          <td className="px-3 py-2">{p.reference_no || '—'}{p.note ? <span className="block text-xs text-gray-500">{p.note}</span> : null}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{p.recorded_by_name || '—'}</td>
                          {canDelete && (
                            <td className="px-3 py-2 text-right">
                              <button onClick={() => handleDelete(p.id)} disabled={saving} className="text-red-600 hover:text-red-700 disabled:opacity-50" title="Remove">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderPaymentsModal;
